import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { notesApi } from "@/api/browser.ts";

/** Private note: only this user sees it, agents never do. Autosaves. */
export function NotePanel({ projectId }: { projectId: string }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["note", projectId], queryFn: () => notesApi.note(projectId) });
  const [text, setText] = useState<string | null>(null);
  const save = useMutation({ mutationFn: (c: string) => notesApi.saveNote(projectId, c), onSuccess: (d) => qc.setQueryData(["note", projectId], d) });
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const value = text ?? q.data?.content ?? "";
  useEffect(() => () => clearTimeout(timer.current), []);
  const onChange = (v: string) => {
    setText(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => save.mutate(v), 800);
  };
  const fmt = (iso: string) => new Intl.DateTimeFormat(i18n.language, { timeStyle: "short", dateStyle: "short" }).format(new Date(iso));
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-border px-3 py-1.5 text-xs text-fg-muted">
        <span>{t("note.private")}</span>
        <span>{save.isPending ? t("note.saving") : q.data?.updatedAt ? `${t("note.saved")} · ${fmt(q.data.updatedAt)}` : ""}</span>
      </div>
      <textarea className="min-h-0 flex-1 resize-none bg-bg p-3 font-mono text-sm leading-6 focus:outline-none" placeholder={t("note.placeholder")} value={value} onChange={(e) => onChange(e.target.value)} maxLength={524288} />
      <div className="border-t border-border px-3 py-1 text-end text-[11px] text-fg-muted" dir="ltr">{value.length.toLocaleString()} / 524,288</div>
    </div>
  );
}
